import jQuery from "jquery";

window.$ = jQuery; // workaround for https://github.com/parcel-bundler/parcel/issues/333

import "bootstrap";

import instantsearch from "instantsearch.js/es";
import {
  searchBox,
  infiniteHits,
  configure,
  stats,
  refinementList,
  numericMenu,
  sortBy,
  currentRefinements,
} from "instantsearch.js/es/widgets";
import { history } from "instantsearch.js/es/lib/routers";

import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import STOP_WORDS from "./utils/stop_words.json";
import I18N from "./i18n.json";

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------
function detectLang() {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang && I18N[urlLang]) return urlLang;
  const browserLang = (navigator.language || "de").split("-")[0];
  return I18N[browserLang] ? browserLang : "de";
}

const lang = detectLang();
function t(key) {
  return (I18N[lang] && I18N[lang][key]) || (I18N["de"] && I18N["de"][key]) || key;
}

// Grade-Labels für abbr-Tooltips auf Zustandscodes
const GRADE_LABELS = {
  "M":   "Mint",
  "NM":  "Near Mint",
  "NM-": "Near Mint minus",
  "EX":  "Excellent",
  "VG+": "Very Good Plus",
  "VG":  "Very Good",
  "VG-": "Very Good minus",
  "G+":  "Good Plus",
  "G":   "Good",
  "F":   "Fair",
  "P":   "Poor",
};

// ---------------------------------------------------------------------------
// Typesense server config
// ---------------------------------------------------------------------------
const anchorParams = new Proxy(
  new URLSearchParams(window.location.hash.replace("#", "")),
  { get: (searchParams, prop) => searchParams.get(prop) }
);

let TYPESENSE_SERVER_CONFIG = {
  apiKey: process.env.TYPESENSE_SEARCH_ONLY_API_KEY,
  nodes: [
    {
      host: anchorParams.host ? anchorParams.host : process.env.TYPESENSE_HOST,
      port: process.env.TYPESENSE_PORT,
      protocol: process.env.TYPESENSE_PROTOCOL,
      path: process.env.TYPESENSE_PATH || "/api",
    },
  ],
  numRetries: 8,
  useServerSideSearchCache: true,
};

if (process.env[`TYPESENSE_HOST_2`]) {
  TYPESENSE_SERVER_CONFIG.nodes.push({
    host: anchorParams.host ? anchorParams.host : process.env[`TYPESENSE_HOST_2`],
    port: process.env.TYPESENSE_PORT,
    protocol: process.env.TYPESENSE_PROTOCOL,
    path: process.env.TYPESENSE_PATH || "/api",
  });
}

if (process.env[`TYPESENSE_HOST_3`]) {
  TYPESENSE_SERVER_CONFIG.nodes.push({
    host: anchorParams.host ? anchorParams.host : process.env[`TYPESENSE_HOST_3`],
    port: process.env.TYPESENSE_PORT,
    protocol: process.env.TYPESENSE_PROTOCOL,
    path: process.env.TYPESENSE_PATH || "/api",
  });
}

if (process.env[`TYPESENSE_HOST_NEAREST`]) {
  TYPESENSE_SERVER_CONFIG.nearestNode = {
    host: anchorParams.host ? anchorParams.host : process.env[`TYPESENSE_HOST_NEAREST`],
    port: process.env.TYPESENSE_PORT,
    protocol: process.env.TYPESENSE_PROTOCOL,
    path: process.env.TYPESENSE_PATH || "/api",
  };
}

// ---------------------------------------------------------------------------
// Dynamic runtime params – passed by Shopware via iframe URL
// collection: maps to TypeSense collection for this sales channel
// shopwareOrigin: the Shopware storefront origin for postMessage (replaces .env.production)
// ---------------------------------------------------------------------------
const _runtimeParams = new URLSearchParams(window.location.search);

// salesChannelId → TypeSense collection mapping
// Keyed by Shopware sales channel ID (lowercase hex, no dashes)
// IDs queried from Shopware API 2026-02-22
const _CHANNEL_COLLECTION_MAP = {
  '0192c2d81410726d91e427fff08483af': 'products_gebrauchte_cds',       // Gebrauchte-CDs-kaufen
  '01954d1789bd7d699d90ead56a591601': 'products_schallplatten',         // Schallplatten
  '019928d2d19f7e5cadf261f1f00be559': 'products_schellack',             // Schellack
  '98432def39fc4624b33213a56b8c944d': 'products_vinyliebhaberei_ebay',  // Vinyliebhaberei - Ebay
  '019928c18ba7791082e68ff526499266': 'products_vinyl',                 // Vinyl
};

const _salesChannelId = (_runtimeParams.get('salesChannelId') || '').toLowerCase();
const INDEX_NAME = _runtimeParams.get('collection')
  || _CHANNEL_COLLECTION_MAP[_salesChannelId]
  || process.env.TYPESENSE_COLLECTION_NAME;

// shopwareOrigin: URL-param (from PHP resolver) → document.referrer → env-var
const _shopwareOrigin = _runtimeParams.get('shopwareOrigin')
  || (document.referrer ? new URL(document.referrer).origin : null)
  || process.env.SHOPWARE_TARGET_ORIGIN
  || null;

// ---------------------------------------------------------------------------
// Format-Label-Mapping (format_group-Token → Anzeige), i18n via i18n.json.
// Spiegel von FormatVocabulary::GROUP_TO_LABEL im Theme-Plugin (Single Source of
// Truth). Seit den distinkten Tokens (v1.2.0) kollektion-UNABHÄNGIG — der frühere
// isCdCollection-Trick für vinyl_maxi_12 ist obsolet (die CD-Collection trägt jetzt
// eigene cd/cd_maxi/cd_box-Tokens). Sprache aus `lang`; de bildet die Basis, die
// aktive Sprache überschreibt; per-Token-Fallback (roher Token) bleibt an der
// Verwendungsstelle (FORMAT_LABELS[x] || x).
// ---------------------------------------------------------------------------
const FORMAT_LABELS = Object.assign(
  {},
  (I18N["de"] && I18N["de"].formatLabels) || {},
  (I18N[lang] && I18N[lang].formatLabels) || {}
);

// Letzter nicht-leerer Suchbegriff (für Desktop-"Letzte Suche"-Feature)
var _lastQuery = "";

// q=-Parameter SOFORT lesen, bevor InstantSearch-Router die URL überschreibt
// (PHP-Resolver trägt den Suchbegriff als ?q= ein; Router entfernt unbekannte Params)
var _initialUrlQuery = new URLSearchParams(window.location.search).get("q") || "";

// Sort-Index-Namen
const SORT_ARTIST_ASC  = `${INDEX_NAME}/sort/artist_sort:asc`;
const SORT_PREIS_ASC   = `${INDEX_NAME}/sort/preis:asc`;
const SORT_PREIS_DESC  = `${INDEX_NAME}/sort/preis:desc`;

// ---------------------------------------------------------------------------
// Stop-word filter
// ---------------------------------------------------------------------------
function queryWithoutStopWords(query) {
  const words = query.replace(/[&/\\#,+()$~%.':*?<>{}]/g, "").split(" ");
  return words
    .map((word) => (STOP_WORDS.includes(word.toLowerCase()) ? null : word))
    .filter((w) => w)
    .join(" ")
    .trim();
}

// ---------------------------------------------------------------------------
// Typesense adapter
// ---------------------------------------------------------------------------
const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
  server: TYPESENSE_SERVER_CONFIG,
  additionalSearchParameters: {
    // track_text mit Gewicht 1 - enthält Tracklisten die NICHT in search_blob sind
    // styles/genres mit Gewicht 2: ein Treffer auf den Stil ("Disco") oder das Genre
    // erscheint mit, rankt aber unter Titel-/Interpret-Treffern. genres steckt zwar
    // schon im search_blob, hier explizit für saubere Gewichtung + exakte Token-Treffer.
    query_by: "title,artist,track_text,search_blob,styles,genres",
    query_by_weights: "4,3,1,1,2,2",
    include_fields:
      "sku,title,artist,artist_slug,artist_has_description,preis,image_path,format_group,cover_grade_code,vinyl_grade_code,genres,group_slug",
    // Nur sichtbare, vollständige Produkte anzeigen – globaler Default-Filter
    filter_by: "visible_any:=true && missing_product:=false",
  },
  collectionSpecificSearchParameters: {
    [SORT_ARTIST_ASC]:  { sort_by: "artist_sort:asc" },
    [SORT_PREIS_ASC]:   { sort_by: "preis:asc" },
    [SORT_PREIS_DESC]:  { sort_by: "preis:desc" },
  },
});

const searchClient = typesenseInstantsearchAdapter.searchClient;

// ---------------------------------------------------------------------------
// InstantSearch setup
// ---------------------------------------------------------------------------
const search = instantsearch({
  searchClient,
  indexName: INDEX_NAME,
  routing: {
    router: history({ cleanUrlOnDispose: true }),
  },
  onStateChange({ uiState, setUiState }) {
    const state = uiState[INDEX_NAME] || {};
    const hasQuery = (state.query || "").trim() !== "";
    const hasRefinements =
      Object.keys(state.refinementList || {}).length > 0 ||
      Object.keys(state.numericMenu || {}).length > 0;
    const isMobile = window.matchMedia("(max-width: 991px)").matches;

    if (hasQuery || hasRefinements) {
      $("#hits-wrapper").removeClass("d-none");
      $("#browse-placeholder").addClass("d-none");
      // Mobile: Filter-Drawer beim Tippen schließen
      if (isMobile) {
        var fs = document.getElementById("filters-section");
        if (fs && fs.classList.contains("show")) {
          fs.classList.remove("show");
        }
      }
    } else {
      // Leere Suche → alle Artikel anzeigen (Typesense liefert bei leerem Query alle Docs)
      $("#hits-wrapper").removeClass("d-none");
      $("#browse-placeholder").addClass("d-none");
    }

    setUiState(uiState);
  },
  future: {
    preserveSharedStateOnUnmount: true,
  },
});

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------
search.addWidgets([
  searchBox({
    container: "#searchbox",
    showSubmit: false,
    showReset: false,
    placeholder: t("searchPlaceholder"),
    autofocus: true,
    cssClasses: {
      input: "form-control searchbox-input",
    },
    queryHook(query, doSearch) {
      const modifiedQuery = queryWithoutStopWords(query);
      if (modifiedQuery.trim() !== "") {
        _lastQuery = modifiedQuery;
        // Shopware-Parent über neue Suchanfrage informieren (cross-origin sessionStorage-Bridge)
        if (window.parent !== window) {
          try {
            window.parent.postMessage(
              { type: "queryChanged", version: "v1", data: { query: modifiedQuery } },
              _shopwareOrigin || "*"
            );
          } catch (_) {}
        }
        doSearch(modifiedQuery);
      } else {
        // Desktop: leere Suche → letzte Suche weiter anzeigen
        const onDesktop = window.matchMedia("(min-width: 992px)").matches;
        if (onDesktop && _lastQuery) {
          doSearch(_lastQuery);
        } else {
          doSearch("");
        }
      }
    },
  }),

  stats({
    container: "#stats",
    templates: {
      text: ({ nbHits, hasNoResults, hasOneResult, processingTimeMS }) => {
        let statsText;
        if (hasNoResults) statsText = t("statsNoResults");
        else if (hasOneResult) statsText = t("statsOneResult");
        else statsText = t("statsManyResults").replace("{n}", nbHits.toLocaleString());
        return `${statsText} ${t("statsTiming").replace("{ms}", processingTimeMS)}`;
      },
    },
  }),

  // Aktive Filter-Tags
  currentRefinements({
    container: "#current-refinements",
    cssClasses: {
      list: "list-unstyled d-flex flex-wrap gap-1 mb-0",
      item: "d-inline-flex align-items-center",
      label: "visually-hidden",
      category: "badge bg-primary d-flex align-items-center gap-1 px-2 py-1",
      delete: "btn-close btn-close-white ms-1",
    },
    transformItems(items) {
      const labelMap = {
        format_group:     t("refinementFormat"),
        cover_grade_code: t("refinementCover"),
        vinyl_grade_code: t("refinementVinyl"),
        genres:           t("refinementGenre"),
        styles:           t("refinementStyle"),
        preis:            t("refinementPrice"),
      };
      return items.map((item) => ({
        ...item,
        label: labelMap[item.attribute] || item.attribute,
      }));
    },
  }),

  // Format
  refinementList({
    container: "#format-refinement-list",
    attribute: "format_group",
    sortBy: ["count:desc", "name:asc"],
    showMore: true,
    showMoreLimit: 20,
    templates: {
      showMoreText: ({ isShowingMore }) =>
        isShowingMore ? t("showLess") : t("showMore"),
    },
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
      showMore: "btn btn-link btn-sm p-0 mt-1 text-decoration-none",
    },
    transformItems(items) {
      return items.map((item) => ({
        ...item,
        label: FORMAT_LABELS[item.label] || item.label,
        highlighted: FORMAT_LABELS[item.highlighted] || item.highlighted,
      }));
    },
  }),

  // Cover-Zustand
  refinementList({
    container: "#cover-grade-refinement-list",
    attribute: "cover_grade_code",
    sortBy: ["name:asc"],
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
    },
  }),

  // Vinyl-Zustand
  refinementList({
    container: "#vinyl-grade-refinement-list",
    attribute: "vinyl_grade_code",
    sortBy: ["name:asc"],
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
    },
  }),

  // Genre
  refinementList({
    container: "#genre-refinement-list",
    attribute: "genres",
    sortBy: ["count:desc", "name:asc"],
    showMore: true,
    showMoreLimit: 30,
    templates: {
      showMoreText: ({ isShowingMore }) =>
        isShowingMore ? t("showLess") : t("showMore"),
    },
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
      showMore: "btn btn-link btn-sm p-0 mt-1 text-decoration-none",
    },
  }),

  // Stil – sofort feuernde Checkbox-Liste wie Genre (KEIN searchable/Lupe);
  // 300+ Werte über limit + showMore gelöst.
  refinementList({
    container: "#style-refinement-list",
    attribute: "styles",
    sortBy: ["count:desc", "name:asc"],
    limit: 12,
    showMore: true,
    showMoreLimit: 40,
    templates: {
      showMoreText: ({ isShowingMore }) =>
        isShowingMore ? t("showLess") : t("showMore"),
    },
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
      showMore: "btn btn-link btn-sm p-0 mt-1 text-decoration-none",
    },
  }),

  // Preis-Filter (numericMenu, da preis kein Facet-Feld)
  numericMenu({
    container: "#price-selector",
    attribute: "preis",
    items: [
      { label: t("filterPriceAll") },
      { label: t("filterPriceUpTo5"),   end: 5 },
      { label: t("filterPrice5to15"),   start: 5,   end: 15 },
      { label: t("filterPrice15to30"),  start: 15,  end: 30 },
      { label: t("filterPrice30to50"),  start: 30,  end: 50 },
      { label: t("filterPrice50to100"), start: 50,  end: 100 },
      { label: t("filterPriceOver100"), start: 100 },
    ],
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      radio: "form-check-input flex-shrink-0",
    },
  }),

  // Sortierung
  sortBy({
    container: "#sort-by",
    items: [
      { label: t("sortRelevance"),  value: INDEX_NAME },
      { label: t("sortArtistAsc"), value: SORT_ARTIST_ASC },
      { label: t("sortPriceAsc"),  value: SORT_PREIS_ASC },
      { label: t("sortPriceDesc"), value: SORT_PREIS_DESC },
    ],
    cssClasses: {
      select: "form-select form-select-sm",
    },
  }),

  // Treffer
  infiniteHits({
    container: "#hits",
    cssClasses: {
      list: "list-unstyled grid-container",
      item: "d-flex flex-column search-result-card bg-white p-3",
      loadMore: "btn btn-outline-primary mx-auto d-block mt-4",
    },
    templates: {
      item: (hit, { html, components }) => html`
        <div class="d-flex flex-column gap-2">

          <!-- Produktbild: volle Breite, proportionale Höhe -->
          ${hit.image_path
            ? html`<img
                src="${hit.image_path}"
                alt="${hit.title || ""}"
                decoding="async"
                loading="lazy"
                class="hit-img-clickable"
                data-sku="${hit.sku || ""}"
                data-title="${(hit.title || "").replace(/"/g, "&quot;")}"
                data-group-slug="${hit.group_slug || ""}"
                style="width:100%;height:auto;display:block;border-radius:6px;"
              />`
            : html`<div class="img-placeholder" style="width:100%;aspect-ratio:1/1;background:#e9ecef;border-radius:6px;"></div>`
          }

          <!-- Info -->
          <div>
            <h6 class="fw-semibold mb-1 hit-title">
              ${components.Highlight({ hit, attribute: "title" })}
            </h6>

            <div class="mb-1" style="font-size:.82rem;font-weight:600;color:#2a1d18;">
              ${hit.artist_has_description && hit.artist_slug
                ? html`<a href="${_shopwareOrigin}/artist/${hit.artist_slug}"
                           target="_top"
                           style="color:#00728c;text-decoration:none;"
                           title="${(hit.artist || "").replace(/"/g, "&quot;")} Schallplatten kaufen">
                           ${components.Highlight({ hit, attribute: "artist" })}
                         </a>`
                : components.Highlight({ hit, attribute: "artist" })
              }
            </div>

            <!-- Badges -->
            <div class="mb-2 d-flex flex-wrap gap-1">
              ${hit.format_group
                ? html`<span class="hit-badge">${FORMAT_LABELS[hit.format_group] || hit.format_group}</span>`
                : ""}
              ${(hit.genres || []).slice(0, 2).map(
                (g) => html`<span class="hit-badge">${g}</span>`
              )}
              ${hit.cover_grade_code
                ? html`<span class="hit-badge" title="${t("refinementCover")}"><span aria-hidden="true">C: </span><abbr title="${GRADE_LABELS[hit.cover_grade_code] ?? hit.cover_grade_code}">${hit.cover_grade_code}</abbr></span>`
                : ""}
              ${hit.vinyl_grade_code
                ? html`<span class="hit-badge" title="${t("refinementVinyl")}"><span aria-hidden="true">V: </span><abbr title="${GRADE_LABELS[hit.vinyl_grade_code] ?? hit.vinyl_grade_code}">${hit.vinyl_grade_code}</abbr></span>`
                : ""}
            </div>

            <div class="small text-muted mb-2">
              <span class="me-2"><span aria-hidden="true">${t("fieldSku")}: </span>${hit.sku || ""}</span>
              ${hit.preis != null
                ? html`<strong>${hit.preis.toFixed(2)}<span aria-hidden="true"> EUR</span></strong>
                  <span class="d-block" style="font-size:.7rem;color:rgba(42,29,24,.5);">inkl. ges. MwSt. zzgl. Versandkosten</span>`
                : ""}
            </div>

            <button
              class="btn btn-sm btn-primary w-100 js-select"
              data-sku="${hit.sku || ""}"
              data-title="${(hit.title || "").replace(/"/g, "&quot;")}"
              data-group-slug="${hit.group_slug || ""}"
              aria-label="${(hit.title || "").replace(/"/g, "&quot;")} – ${t("selectButton")}"
            >
              <span aria-hidden="true">${t("selectButton")}</span>
            </button>
          </div>

        </div>
      `,
      empty: (data, { html }) =>
        html`<p class="text-muted">${t("noResultsText")
          .replace("{q}", data.query)}</p>`,
    },
  }),

  configure({
    hitsPerPage: 16,
  }),
]);

// ---------------------------------------------------------------------------
// Event handlers
// ---------------------------------------------------------------------------

// Shared navigation function: navigate top-level Shopware window to product page.
// Works cross-origin without requiring the Shopware plugin JS to be compiled.
function _doSelect(sku, title, groupSlug) {
  // Aktuelle Suche merken, damit "Zurück" sie wiederherstellt
  if (_lastQuery) {
    sessionStorage.setItem("ts_return_query", _lastQuery);
  }

  // Step 7: trägt der Treffer ein group_slug (7a, im Typesense-Doc), tief auf die Gruppen-PDP
  // /article/{group_slug}#{sku} verlinken (Anker == nackte SKU, von Google entfernt → SEO-silent;
  // die Empfangsseite committet die Zeile per location.hash). Ohne group_slug → klassischer Resolver.
  var target = groupSlug
    ? "/article/" + groupSlug + "#" + sku
    : "/typesense/resolve/" + encodeURIComponent(sku);

  if (_shopwareOrigin && window.top && window.top !== window) {
    window.top.location.href = _shopwareOrigin + target;
  } else if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      { type: "select", version: "v1", ts: Date.now(), data: { sku, title, groupSlug } },
      _shopwareOrigin || "*"
    );
  } else {
    console.log("Selected:", { sku, title, groupSlug });
    alert(`${sku} – ${title}`);
  }
}

// Inject hover-zoom CSS once (desktop only via media query; mobile has no hover)
(function () {
  var style = document.createElement("style");
  style.textContent = [
    ".hit-img-clickable { cursor: pointer; }",
    "@media (min-width: 768px) {",
    "  .hit-img-clickable { transition: transform 0.2s ease, box-shadow 0.2s ease; }",
    "  .hit-img-clickable:hover { transform: scale(2.0); box-shadow: 0 8px 24px rgba(0,0,0,0.4); z-index: 100; position: relative; }",
    "}",
  ].join("\n");
  document.head.appendChild(style);
}());

search.on("render", function () {
  // "Auswählen" button
  $("#hits .js-select")
    .off("click")
    .on("click", (e) => {
      const sku       = e.currentTarget.getAttribute("data-sku");
      const title     = e.currentTarget.getAttribute("data-title");
      const groupSlug = e.currentTarget.getAttribute("data-group-slug");
      _doSelect(sku, title, groupSlug);
    });

  // Image click = same as Auswählen
  $("#hits .hit-img-clickable")
    .off("click")
    .on("click", (e) => {
      const sku       = e.currentTarget.getAttribute("data-sku");
      const title     = e.currentTarget.getAttribute("data-title");
      const groupSlug = e.currentTarget.getAttribute("data-group-slug");
      _doSelect(sku, title, groupSlug);
    });

  // Dynamically set transform-origin so zoomed image never overflows iframe edge:
  // images in the right half of the viewport expand to the left, left half to the right.
  // Only active on desktop (matchMedia guard keeps mobile clean).
  if (window.matchMedia("(min-width: 768px)").matches) {
    $("#hits .hit-img-clickable")
      .off("mouseover.zoom")
      .on("mouseover.zoom", function () {
        var rect = this.getBoundingClientRect();
        var viewportWidth = document.documentElement.clientWidth;
        var imgCenterX = rect.left + rect.width / 2;
        var xOrigin = imgCenterX > viewportWidth / 2 ? "right" : "left";
        this.style.transformOrigin = xOrigin + " top";
      });
  }
});

search.start();

// ---------------------------------------------------------------------------
// Dynamic iframe height: send body.scrollHeight to parent on every resize.
// The Twig inline script listens for these messages and sets iframe.style.height.
// ---------------------------------------------------------------------------
if (window.parent !== window && typeof ResizeObserver !== "undefined") {
  var _resizeTimer;
  var _resizeObserver = new ResizeObserver(function () {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      var height = document.body.scrollHeight;
      window.parent.postMessage(
        { type: "resize", version: "v1", data: { height: height } },
        _shopwareOrigin || "*"
      );
    }, 80);
  });
  _resizeObserver.observe(document.body);
}

// ---------------------------------------------------------------------------
// Initial render: show hits-wrapper if page was opened with a query in the URL
// (onStateChange is NOT called for the initial URL-restored state)
//
// Compatibility: Shopware Twig may encode query as {collectionName}[query]=...
// using a different collection name than INDEX_NAME (happens when PHP resolver
// hasn't been updated yet). We detect any *[query] param as fallback.
// ---------------------------------------------------------------------------
(function () {
  var _firstRenderDone = false;
  search.on("render", function onFirstRender() {
    if (_firstRenderDone) return;
    _firstRenderDone = true;

    // Prüfen ob der User von einer Produktseite zurücknavigiert ist.
    // Dann hat die gespeicherte SPA-Suche Vorrang vor dem URL-Parameter
    // (der enthält noch die ursprüngliche Shopware-Suchanfrage, nicht die
    // zuletzt im SPA eingegebene Suche).
    var returnQuery = sessionStorage.getItem("ts_return_query");
    if (returnQuery) {
      sessionStorage.removeItem("ts_return_query");
      _lastQuery = returnQuery;
      search.setUiState(function (prev) {
        return Object.assign({}, prev, {
          [INDEX_NAME]: Object.assign({}, prev[INDEX_NAME] || {}, { query: returnQuery }),
        });
      });
      $("#hits-wrapper").removeClass("d-none");
      $("#browse-placeholder").addClass("d-none");
      return;
    }

    var urlParams = new URLSearchParams(window.location.search);

    // Primary: correct collection name
    var initialQuery = urlParams.get(INDEX_NAME + "[query]") || "";

    // Fallback: any {something}[query] param (e.g. old Twig with wrong collection name)
    if (!initialQuery) {
      urlParams.forEach(function (value, key) {
        if (!initialQuery && /\[query\]$/.test(key) && value.trim()) {
          initialQuery = value.trim();
        }
      });
    }

    // Fallback: plain ?q= param (captured before InstantSearch router could overwrite URL)
    if (!initialQuery) {
      initialQuery = _initialUrlQuery;
    }

    if (initialQuery) {
      _lastQuery = initialQuery;
      // Shopware-Parent sofort informieren → sessionStorage für "Zurück"-Bar setzen
      if (window.parent !== window) {
        try {
          window.parent.postMessage(
            { type: "queryChanged", version: "v1", data: { query: initialQuery } },
            _shopwareOrigin || "*"
          );
        } catch (_) {}
      }
      // Trigger search with the found query (also populates the searchBox widget)
      search.setUiState(function (prev) {
        return Object.assign({}, prev, {
          [INDEX_NAME]: Object.assign({}, prev[INDEX_NAME] || {}, { query: initialQuery }),
        });
      });
    }
    // Immer Artikel anzeigen – bei leerem Query zeigt Typesense alle Docs
    $("#hits-wrapper").removeClass("d-none");
    $("#browse-placeholder").addClass("d-none");
  });
}());

// ---------------------------------------------------------------------------
// postMessage listener: Shopware → SPA
// Payload: { type:"search", version:"v1", ts, data:{query} }
// Sent by Shopware after iframe load when user came from the header search
// ---------------------------------------------------------------------------
(function () {
  var allowedOrigin = _shopwareOrigin;

  window.addEventListener("message", function (e) {
    if (allowedOrigin && e.origin !== allowedOrigin) return;
    var msg = e.data;
    if (!msg || msg.type !== "search" || msg.version !== "v1") return;
    var query = msg.data ? String(msg.data.query || "").trim() : "";

    // Immer Artikel anzeigen – leere Query liefert alle Docs
    $("#hits-wrapper").removeClass("d-none");
    $("#browse-placeholder").addClass("d-none");

    search.setUiState(function (prev) {
      return Object.assign({}, prev, {
        [INDEX_NAME]: Object.assign({}, prev[INDEX_NAME] || {}, { query: query }),
      });
    });
  });
}());

$(function () {
  // Seite mit Sprachstring befüllen
  document.title = t("pageTitle");
  $("#browse-placeholder p").text(t("browsePlaceholder"));
});

// ---------------------------------------------------------------------------
// postMessage: Shopware → SPA – Filter-Toggle
// Payload: { type:"toggleFilter", version:"v1" }
// Shopware sendet dies vom mobilen "Filter"-FAB-Button.
// SPA toggelt die Bootstrap-Collapse-Sektion und scrollt – falls geöffnet –
// das Parent-Window an den Iframe-Anfang.
// ---------------------------------------------------------------------------
(function () {
  var allowedOrigin = _shopwareOrigin;

  window.addEventListener("message", function (e) {
    if (allowedOrigin && e.origin !== allowedOrigin) return;
    var msg = e.data;
    if (!msg || msg.type !== "toggleFilter" || msg.version !== "v1") return;

    var fs = document.getElementById("filters-section");
    if (!fs) return;

    var isOpen = fs.classList.contains("show");
    if (isOpen) {
      fs.classList.remove("show");
    } else {
      fs.classList.add("show");
    }

    // Wenn der Filter eingeblendet wird → Parent nach oben scrollen
    if (!isOpen && window.parent !== window) {
      try {
        window.parent.postMessage(
          { type: "scrollToIframeTop", version: "v1" },
          _shopwareOrigin || "*"
        );
      } catch (_) {}
    }
  });
}());
